/**
 * When this browser should treat the session as over, on its OWN clock.
 *
 * The server says when the session ends (`expiresAt`) and what time it was when it said so
 * (`serverNow`). The gap between the two is what is left; adding that to this machine's `now`
 * gives a deadline that lands on time even if this computer's clock is wrong.
 *
 * Comparing `expiresAt` to `Date.now()` directly would be off by however far the clock has
 * drifted — a laptop five minutes fast would sign somebody out five minutes early, and one an
 * hour slow would leave a dead page on screen for an hour.
 *
 * Null when the server did not say, which means: do not count down at all. The 401 handling
 * still catches the end on the next request.
 */
export function deadlineFrom(
  expiresAt: string | null | undefined,
  serverNow: string | null | undefined,
  localNow: number,
): number | null {
  if (!expiresAt || !serverNow) return null;
  const ends = new Date(expiresAt).getTime();
  const then = new Date(serverNow).getTime();
  if (!Number.isFinite(ends) || !Number.isFinite(then)) return null;
  return localNow + (ends - then);
}

/**
 * Where to send somebody after they sign back in — the page they were on, if it is safe to.
 *
 * Only a path inside the app is honoured. `//evil.example` is a valid "path" to a browser and
 * means another site entirely, so it is refused along with anything absolute; and `/login` is
 * refused because returning to the sign-in page after signing in is a loop.
 */
export function safeReturnPath(from: string | null | undefined): string | null {
  if (!from) return null;
  if (!from.startsWith('/')) return null;
  if (from.startsWith('//') || from.startsWith('/\\')) return null;
  if (from === '/login' || from.startsWith('/login?') || from.startsWith('/login/')) return null;
  return from;
}
